import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  FormControlLabel,
  InputAdornment,
  Link,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { type GridColDef, type GridRenderCellParams } from '@mui/x-data-grid';
import { listAuditLog, type AuditLogRow } from '../lib/api';
import { WafDataGrid } from '../components/WafDataGrid';

function useDebounced<T>(value: T, ms = 150): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

function prettyField(field: string | null, source?: string | null): string {
  if (!field) {
    if (source?.startsWith('import:')) return 'Imported from Evessio';
    return 'Whole entry';
  }
  return field.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function prettySource(source: string): string {
  if (source === 'manual') return 'Manual edit';
  if (source === 'evessio_sync') return 'Evessio sync';
  if (source.startsWith('change_request:')) return `Change request #${source.split(':')[1]}`;
  return source;
}

function fmtValue(v: AuditLogRow['value_before']): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'string') return v === '' ? '(empty)' : v;
  return String(v);
}

interface Row extends AuditLogRow {
  id: number;
}

export function AuditLog() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounced(query, 150);
  const [grouped, setGrouped] = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    let cancelled = false;
    listAuditLog()
      .then((data) => {
        if (cancelled) return;
        setRows(data as Row[]);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const textFiltered = useMemo(() => {
    const q = debouncedQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      return (
        (r.actor_display_name || '').toLowerCase().includes(q) ||
        (r.actor || '').toLowerCase().includes(q) ||
        (r.project_title || '').toLowerCase().includes(q) ||
        (r.nomination_code || '').toLowerCase().includes(q) ||
        (r.architect_company_name || '').toLowerCase().includes(q) ||
        (r.field || '').toLowerCase().includes(q) ||
        prettySource(r.source).toLowerCase().includes(q)
      );
    });
  }, [rows, debouncedQuery]);

  // When grouping is on, collapse rows that share an "import:N" source
  // into a single summary row. Expanding a group inserts its real rows
  // inline directly underneath the summary, in newest-first order.
  const filtered = useMemo(() => {
    if (!grouped) return textFiltered;

    // 1. Sort everything newest-first so the resulting list is already
    //    in the order the user expects to see.
    const sorted = [...textFiltered].sort((a, b) =>
      a.created_at < b.created_at ? 1 : a.created_at > b.created_at ? -1 : 0
    );

    // 2. Walk the sorted list. The first time we see an `import:N`
    //    source, emit the summary row for it (sized using all rows in
    //    that bucket from the original list). Subsequent rows in the
    //    same bucket only get emitted when the bucket is expanded —
    //    which keeps the summary visually ABOVE its children.
    const bucketSizes = new Map<string, number>();
    for (const r of textFiltered) {
      if (r.source?.startsWith('import:')) {
        bucketSizes.set(r.source, (bucketSizes.get(r.source) ?? 0) + 1);
      }
    }

    // Stable per-source negative id allocator. Parsing a numeric suffix
    // out of "import:N" was fragile — non-numeric suffixes collapsed to
    // 0 (collision) and re-renders weren't guaranteed to give the same
    // id. A Map keyed on the full source string guarantees uniqueness
    // and stable identity across renders, which DataGrid needs for row
    // selection / animations.
    const bucketIdMap = new Map<string, number>();
    let nextBucketId = -1;
    const allocateBucketId = (src: string): number => {
      const existing = bucketIdMap.get(src);
      if (existing !== undefined) return existing;
      const id = nextBucketId;
      bucketIdMap.set(src, id);
      nextBucketId -= 1;
      return id;
    };

    const seenBuckets = new Set<string>();
    const out: Row[] = [];
    for (const r of sorted) {
      const src = r.source;
      if (src?.startsWith('import:')) {
        if (!seenBuckets.has(src)) {
          seenBuckets.add(src);
          const count = bucketSizes.get(src) ?? 1;
          out.push({
            ...r,
            id: allocateBucketId(src),
            field: null,
            project_title: `Uploaded ${count} entr${count === 1 ? 'y' : 'ies'}`,
            architect_company_name: null,
            nomination_code: null,
            entity_type: 'nomination',
            entity_id: src,
            value_before: null,
            value_after: null,
          });
        }
        if (expanded.has(src)) out.push(r);
      } else {
        out.push(r);
      }
    }

    return out;
  }, [textFiltered, grouped, expanded]);

  const columns: GridColDef<Row>[] = [
    {
      field: 'created_at',
      headerName: 'When',
      width: 170,
      valueGetter: (_v, row) => new Date(row.created_at),
      renderCell: (p: GridRenderCellParams<Row>) => (
        <Typography variant="body2" color="text.secondary">
          {new Date(p.row.created_at).toLocaleString()}
        </Typography>
      ),
    },
    {
      field: 'actor',
      headerName: 'Person',
      width: 180,
      // Prefer the resolved display name from app_users (looked up via
      // the row's actor_user_id FK); fall back to whatever raw label was
      // stamped at write time (often an email, sometimes a Zitadel sub
      // for older rows); finally show "Unknown" rather than a blank cell.
      valueGetter: (_v, row) => row.actor_display_name || row.actor || 'Unknown',
    },
    {
      field: 'project_title',
      headerName: 'Project',
      flex: 1.2,
      minWidth: 220,
      renderCell: (p: GridRenderCellParams<Row>) => {
        // Grouped-ingest summary row (synthetic, negative id)
        if (p.row.id < 0 && p.row.entity_id?.startsWith('import:')) {
          const isOpen = expanded.has(p.row.entity_id);
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 600 }}>
              {isOpen ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
              {p.row.project_title}
            </Box>
          );
        }
        if (p.row.entity_type !== 'nomination' || !p.row.entity_id) {
          return <span>—</span>;
        }
        const label = p.row.project_title || p.row.nomination_code || p.row.entity_id;
        return (
          <Link
            component={RouterLink}
            to={`/entry/${p.row.entity_id}`}
            onClick={(e) => e.stopPropagation()}
            underline="hover"
            sx={{ color: 'text.primary' }}
          >
            {label}
          </Link>
        );
      },
    },
    {
      field: 'field',
      headerName: 'Field',
      width: 180,
      valueGetter: (_v, row) => prettyField(row.field, row.source),
    },
    {
      field: 'change',
      headerName: 'Change',
      flex: 1.4,
      minWidth: 280,
      sortable: false,
      renderCell: (p: GridRenderCellParams<Row>) => (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline', overflow: 'hidden' }}>
          <Typography
            variant="body2"
            sx={{
              color: 'text.disabled',
              textDecoration: 'line-through',
              maxWidth: 180,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={fmtValue(p.row.value_before)}
          >
            {fmtValue(p.row.value_before)}
          </Typography>
          <Typography variant="caption" color="text.secondary">→</Typography>
          <Typography
            variant="body2"
            sx={{
              maxWidth: 180,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={fmtValue(p.row.value_after)}
          >
            {fmtValue(p.row.value_after)}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'source',
      headerName: 'Source',
      width: 170,
      valueGetter: (_v, row) => prettySource(row.source),
    },
  ];

  return (
    <Stack spacing={3}>
      <Typography variant="h3" component="h1" align="center">
        Audit log
      </Typography>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        flexWrap="wrap"
        useFlexGap
      >
        <TextField
          size="small"
          placeholder="Search person, project, field, source…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ flex: 1, width: { xs: '100%', sm: 'auto' }, maxWidth: { xs: '100%', sm: 520 } }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={grouped}
              onChange={(e) => setGrouped(e.target.checked)}
              size="small"
            />
          }
          label="Group ingests"
          sx={{ mr: 0 }}
        />
        <Box sx={{ flex: 1, display: { xs: 'none', sm: 'block' } }} />
        <Typography variant="body2">
          {filtered.length} of {rows.length}
        </Typography>
      </Stack>

      <Paper
        sx={(theme) => ({
          // Subtle shared background for an open import group — applied
          // via a descendant selector so we don't have to merge into the
          // shared dataGridSx (which is typed strictly).
          '& .audit-group-row, & .audit-group-row:hover': {
            backgroundColor: theme.palette.mode === 'dark'
              ? 'rgba(255,255,255,0.04)'
              : 'rgba(0,0,0,0.03)',
          },
        })}
      >
        <WafDataGrid<Row>
          rows={filtered}
          columns={columns}
          loading={loading}
          density="standard"
          onRowClick={(p) => {
            const r = p.row as Row;
            if (r.id < 0 && r.entity_id?.startsWith('import:')) {
              setExpanded((prev) => {
                const next = new Set(prev);
                if (next.has(r.entity_id!)) next.delete(r.entity_id!);
                else next.add(r.entity_id!);
                return next;
              });
            }
          }}
          // Tags rows that are part of a currently-expanded import group —
          // both the summary row and each of its children — so the wrapper
          // Paper can give them a subtle shared background.
          getRowClassName={(params) => {
            const r = params.row as Row;
            const isSummaryOpen = r.id < 0 && r.entity_id?.startsWith('import:') && expanded.has(r.entity_id);
            const isExpandedChild = r.id > 0 && r.source?.startsWith('import:') && expanded.has(r.source);
            return isSummaryOpen || isExpandedChild ? 'audit-group-row' : '';
          }}
          pageSizeOptions={[25, 50, 100, 250]}
          initialState={{
            pagination: { paginationModel: { pageSize: 50 } },
            sorting: { sortModel: [{ field: 'created_at', sort: 'desc' }] },
          }}
        />
      </Paper>
    </Stack>
  );
}
